import pempemMark from "../../rsc/ic_pempem.png";

/**
 * Burns the provenance stamp into the pixels of a delivery photo.
 *
 * The modal draws the same stamp as DOM overlays, but those live only in the
 * page: a downloaded or forwarded file carries none of it. Anything that has to
 * survive leaving the app — the verification mark, where the truck was, when —
 * has to be in the image itself. Metadata is not an option; the upload pipeline
 * strips EXIF.
 */

export type WatermarkContent = {
    /** Wordmark copy, e.g. "Pempem Verifies". */
    copy: string
    coordinates?: string
    /** Already formatted with its unit and suffix, e.g. "14.2 km from mill". */
    distance?: string
    /** Village and administrative trail. */
    place?: string
    timestamp?: string
}

const loadImage = (src: string, crossOrigin?: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const image = new Image();
        // Must be set before `src`, and the bucket has to answer with
        // Access-Control-Allow-Origin, or the canvas is tainted and cannot export.
        if (crossOrigin) image.crossOrigin = crossOrigin;
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error(`Could not load image: ${src}`));
        image.src = src;
    });

const PLATE_FILL = 'rgba(15, 48, 51, 0.72)';
const MONO_STACK = '"Geist Mono", ui-monospace, monospace';
const ELLIPSIS = '…';

/** Drops characters until the text plus an ellipsis fits. Set the font first. */
const truncateToWidth = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string => {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let clipped = text;
    while (clipped.length > 1 && ctx.measureText(clipped + ELLIPSIS).width > maxWidth) {
        clipped = clipped.slice(0, -1);
    }
    return clipped + ELLIPSIS;
};

/**
 * Greedy word wrap bounded to `maxLines`. An administrative trail can be far
 * wider than the photo, and canvas will happily draw straight off the edge.
 */
const wrapText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    maxLines: number,
): string[] => {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let current = '';
    let overflowed = false;

    for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        // The first word of a line always goes on it, however wide — the
        // per-line clip below is what keeps it inside the plate.
        if (!current || ctx.measureText(candidate).width <= maxWidth) {
            current = candidate;
            continue;
        }
        if (lines.length === maxLines - 1) {
            overflowed = true;
            break;
        }
        lines.push(current);
        current = word;
    }

    if (current) lines.push(current);

    const lastIndex = lines.length - 1;
    return lines.map((line, index) =>
        truncateToWidth(ctx, overflowed && index === lastIndex ? `${line} ${ELLIPSIS}` : line, maxWidth));
};

/** Top-right mark: logo then wordmark, vertically centred on each other. */
const drawBadge = (
    ctx: CanvasRenderingContext2D,
    logo: HTMLImageElement,
    copy: string,
    unit: number,
    inset: number,
) => {
    const padX = 20 * unit;
    const padY = 14 * unit;
    const gap = 14 * unit;
    const titleSize = 26 * unit;
    const logoHeight = 34 * unit;
    const logoWidth = logoHeight * ((logo.naturalWidth / logo.naturalHeight) || 1);
    const height = Math.max(titleSize, logoHeight) + padY * 2;

    ctx.font = `700 ${titleSize}px ${MONO_STACK}`;
    const maxTitleWidth = ctx.canvas.width - inset * 2 - padX * 2 - logoWidth - gap;
    const title = truncateToWidth(ctx, copy.toUpperCase(), maxTitleWidth);
    const width = padX * 2 + logoWidth + gap + ctx.measureText(title).width;
    const left = ctx.canvas.width - inset - width;

    ctx.fillStyle = PLATE_FILL;
    ctx.fillRect(left, inset, width, height);
    ctx.drawImage(logo, left + padX, inset + (height - logoHeight) / 2, logoWidth, logoHeight);

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(title, left + padX + logoWidth + gap, inset + height / 2);
};

/** Bottom plate, inset on both sides so it sits centred, text flush right. */
const drawPlate = (
    ctx: CanvasRenderingContext2D,
    content: WatermarkContent,
    unit: number,
    inset: number,
) => {
    const pad = 28 * unit;
    const lineGap = 10 * unit;
    const width = ctx.canvas.width - inset * 2;
    const maxTextWidth = width - pad * 2;

    const entries = [
        { text: content.coordinates, size: 36 * unit, weight: '700 ', color: '#ffffff', maxLines: 1 },
        { text: content.distance, size: 24 * unit, weight: '', color: 'rgba(255, 255, 255, 0.9)', maxLines: 1 },
        { text: content.place, size: 24 * unit, weight: '', color: 'rgba(255, 255, 255, 0.88)', maxLines: 2 },
        { text: content.timestamp, size: 22 * unit, weight: '', color: 'rgba(255, 255, 255, 0.72)', maxLines: 1 },
    ];

    // Measure with the font that will draw each line, and wrap before the plate
    // is sized — its height depends on how many lines the wrap produced.
    const lines = entries.flatMap(entry => {
        if (!entry.text) return [];
        const font = `${entry.weight}${entry.size}px ${MONO_STACK}`;
        ctx.font = font;
        return wrapText(ctx, entry.text, maxTextWidth, entry.maxLines)
            .map(text => ({ text, font, size: entry.size, color: entry.color }));
    });
    if (lines.length === 0) return;

    const textHeight = lines.reduce((sum, line) => sum + line.size, 0) + lineGap * (lines.length - 1);
    const height = pad * 2 + textHeight;
    const top = ctx.canvas.height - inset - height;

    ctx.fillStyle = PLATE_FILL;
    ctx.fillRect(inset, top, width, height);

    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    const right = inset + width - pad;
    let y = top + pad;
    lines.forEach(line => {
        ctx.font = line.font;
        ctx.fillStyle = line.color;
        ctx.fillText(line.text, right, y);
        y += line.size + lineGap;
    });
};

export const watermarkPhoto = async (url: string, content: WatermarkContent): Promise<Blob> => {
    const [photo, logo] = await Promise.all([
        loadImage(url, 'anonymous'),
        loadImage(pempemMark),
    ]);

    const canvas = document.createElement('canvas');
    canvas.width = photo.naturalWidth;
    canvas.height = photo.naturalHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');

    ctx.drawImage(photo, 0, 0);

    // Every measurement scales off the image width, so the stamp reads the same
    // on a 1200px phone shot and on a 4000px upload.
    const unit = canvas.width / 1000;
    // Gap between the photo edge and both overlays, as in the modal.
    const inset = 36 * unit;

    drawBadge(ctx, logo, content.copy, unit, inset);
    drawPlate(ctx, content, unit, inset);

    return new Promise((resolve, reject) => {
        // Throws SecurityError synchronously when the source image tainted the
        // canvas; the caller falls back to the unstamped original.
        try {
            canvas.toBlob(
                blob => blob ? resolve(blob) : reject(new Error('Canvas produced no blob')),
                'image/jpeg',
                0.92,
            );
        } catch (error) {
            reject(error);
        }
    });
};
